import React from "react";

/**
 * Without this, any throw during render unmounts the whole tree and the user
 * sees a blank page with no clue what failed — in a production build React
 * prints no component stack unless a boundary catches the error. The
 * componentStack logged here is what names the offending component.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, recoveredFor: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  /**
   * Clears the fallback on navigation. This is a prop rather than a `key` on
   * the element deliberately: a changing key would remount the entire routed
   * subtree on every navigation, throwing away component state and creating
   * exactly the mount/unmount churn that produces the crash this catches.
   */
  componentDidUpdate(prevProps) {
    if (prevProps.resetKey !== this.props.resetKey) {
      // New page: clear any fallback and re-arm the single retry below.
      if (this.state.error || this.state.recoveredFor !== null) {
        this.setState({ error: null, recoveredFor: null });
      }
    }
  }

  componentDidCatch(error, info) {
    // An unmount-phase throw (apexcharts destroy() on a chart that never
    // finished rendering) comes from the tree being REPLACED — the incoming
    // page is fine, so showing a fallback would hide a healthy page. Retry
    // once; a page that genuinely cannot render throws again on the retry and
    // the fallback then sticks.
    if (this.state.recoveredFor !== this.props.resetKey) {
      this.setState({ error: null, recoveredFor: this.props.resetKey });
    }
    console.error(
      "[ErrorBoundary] crashed at",
      window.location.pathname,
      "\nerror:",
      error,
      "\ncomponentStack:",
      info && info.componentStack
    );
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div style={{ padding: "60px 24px", textAlign: "center", fontFamily: "inherit" }}>
        <p style={{ fontSize: 15, color: "#111827", marginBottom: 8 }}>
          Something went wrong on this page.
        </p>
        <p style={{ fontSize: 13, color: "#6b7280", marginBottom: 20 }}>
          {String((error && error.message) || error)}
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          style={{
            padding: "8px 18px",
            fontSize: 13,
            color: "#ffffff",
            background: "#3953fb",
            border: "none",
            borderRadius: 8,
            cursor: "pointer"
          }}
        >
          Reload
        </button>
      </div>
    );
  }
}

export default ErrorBoundary;
